import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { PartViewerPageData } from '@frontend/api';
import {
  Button,
  ButtonGroup,
  HStack,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from '@frontend/legacy-components';

import { getQuoteItem } from 'modules/quotePartViewer/data/reducer';
import { useQuotePartViewerContext } from 'modules/quotePartViewer/quotePartViewerContext';
import { partLibraryViewerPage } from 'modules/viewerPage/data/actions';
import {
  getActivePart,
  getMultiLevelBom,
  getPartInformation,
  getIsLoading,
} from 'modules/viewerPage/data/reducer';
import { MultiLevelBomData } from 'modules/viewerPage/part/bomTab/types';

import { PartSetupStateChangedBy } from '../utils/partSetupState';

export const getBestSetupPartFileUuid = (part?: PartViewerPageData) => {
  const partFiles = part?.partFiles || [];
  const primaryFile = partFiles.find((partFile) => partFile.isPrimaryFile);

  return (
    partFiles.find((partFile) => !!partFile.technicalDrawingId)
      ?.technicalDrawingId ||
    (primaryFile?.fileType === 'pdf' ? primaryFile.uuid : undefined) ||
    partFiles.find((partFile) => partFile.fileType === 'pdf')?.uuid ||
    partFiles[0]?.uuid
  );
};

export type PartSetupStepperProps = {
  firstInputRef?: React.RefObject<HTMLDivElement>;
};

const vstackLayoutProps = {
  align: 'stretch',
  paddingLeft: 2,
  paddingRight: 2,
  paddingTop: 1,
  paddingBottom: 1.5,
};

const buttonGroupLayoutProps = {
  spacing: 1.5,
  justify: 'space-between',
};

const buttonLayoutProps = {
  flex: '1 0 0',
};

export const PartSetupStepper = ({ firstInputRef }: PartSetupStepperProps) => {
  const dispatch = useDispatch();

  const isLoading = useSelector(getIsLoading);

  const quoteItem = useSelector(getQuoteItem);
  const activePart: PartViewerPageData = useSelector(getActivePart);
  const partInformation: PartViewerPageData[] = useSelector(getPartInformation);
  const multiLevelBom = useSelector(getMultiLevelBom) as MultiLevelBomData;

  const { state: partSetupState, setState: setPartSetupState } =
    useQuotePartViewerContext();
  const { lastChangedBy } = partSetupState;

  const [currentPartPosition, setCurrentPartPosition] = useState<number>(0);
  const [focusInput, setFocusInput] = useState(false);
  const loadingRender = useRef(false);

  // This returns partInformation reordered based on the order of parts in the flattened multi level BOM
  const currentOrderedPartInformation: PartViewerPageData[] = useMemo(() => {
    const flattenedBomPartIds: Set<string> = new Set();
    const bomStack = [multiLevelBom];

    while (bomStack.length) {
      const bomNode = bomStack.shift();
      if (!bomNode?.partUuid) {
        break;
      }
      // Only add children if node is not already in Set -- prevents adding duplicate children to stack
      if (
        bomNode.children?.length &&
        !flattenedBomPartIds.has(bomNode.partUuid)
      ) {
        bomStack.push(...bomNode.children);
      }
      flattenedBomPartIds.add(bomNode.partUuid);
    }

    const a: PartViewerPageData[] = Array.from(flattenedBomPartIds).map(
      (bomPartId) =>
        partInformation.find((partInfo) => partInfo.uuid === bomPartId),
    );
    return a;
  }, [multiLevelBom, partInformation]);

  const bestPreviousSetupPartFileUuid = useMemo(
    () =>
      getBestSetupPartFileUuid(
        currentOrderedPartInformation[currentPartPosition - 1],
      ),
    [currentOrderedPartInformation, currentPartPosition],
  );

  // Call for best file for next part in assembly
  const bestNextSetupPartFileUuid = useMemo(
    () =>
      getBestSetupPartFileUuid(
        currentOrderedPartInformation[currentPartPosition + 1],
      ),
    [currentOrderedPartInformation, currentPartPosition],
  );

  // To focus first input in the PartDescriptorForm on keyboard control flow, part 1:
  // This handles focusing the input when changing parts within a quoteItem
  useEffect(() => {
    if (focusInput) {
      firstInputRef?.current?.querySelector('input')?.focus();
      setFocusInput(false);
    }
  }, [focusInput, setFocusInput, firstInputRef]);

  // To focus first input in the PartDescriptorForm on keyboard control flow, part 2:
  // When changing quoteItems via the partStepper, this component is remounted via the viewer route change
  // This useEffect waits for the 'isLoading' state to occur
  // The order is:
  //    1. PartSetupStepper mounts (isLoading = false)
  //    2. TDP is fetched (isLoading = true), skeleton UI blows away the Input w/ the firstInputRef
  //    3. When TDP/activePart resolves (isLoading = false), firstInputRef.current has the Input again and can focus
  // This flow only occurs when the route contains 'part_setup=2', triggered by keyboard flow
  useEffect(() => {
    if (isLoading) {
      loadingRender.current = true;
      return;
    }

    if (!loadingRender.current) {
      return;
    }

    loadingRender.current = false;

    if (lastChangedBy === PartSetupStateChangedBy.Keyboard) {
      firstInputRef?.current?.querySelector('input')?.focus();
    }
  }, [firstInputRef, isLoading, lastChangedBy]);

  // When activePart changes via:
  //    1. Navigation through the part stepper "Next" button
  //    2. User interaction with BOM tree
  //    3. Through opening a part in QPV via the BAQ
  // Update currentPartPosition based on the part's location in the flattened multi-level BOM
  useEffect(() => {
    setCurrentPartPosition(
      currentOrderedPartInformation.findIndex(
        (partInfo) => partInfo?.uuid === activePart?.uuid,
      ) ?? 0,
    );
  }, [activePart, currentOrderedPartInformation]);

  const handlePreviousItem = useCallback(
    (changedBy: PartSetupStateChangedBy) => {
      if (currentPartPosition > 0) {
        dispatch(
          partLibraryViewerPage.setActiveUUID({
            forceFocus: true,
            partFileUuid: bestPreviousSetupPartFileUuid,
            partUuid:
              currentOrderedPartInformation[currentPartPosition - 1]?.uuid ??
              null,
          }),
        );
        // If submit called via keyboard ('enter' or 'space'), focus the first input of next part
        setPartSetupState({ lastChangedBy: changedBy, gotoLast: false });
        changedBy === PartSetupStateChangedBy.Keyboard && setFocusInput(true);
      } else if (quoteItem?.previous) {
        const quoteItemId = quoteItem.previous.id;
        //Update history, reset searchTail, and dispatch new searchTail and quoteItemId
        //Note: This will trigger useEffect above that dispatches receiveQuoteItemId
        setPartSetupState({
          quoteItemId,
          lastChangedBy: changedBy,
          gotoLast: true,
        });
      }
    },
    [
      dispatch,
      quoteItem,
      currentPartPosition,
      currentOrderedPartInformation,
      bestPreviousSetupPartFileUuid,
      setPartSetupState,
    ],
  );

  const handleNextItem = useCallback(
    (changedBy: PartSetupStateChangedBy) => {
      if (currentOrderedPartInformation.length > currentPartPosition + 1) {
        dispatch(
          partLibraryViewerPage.setActiveUUID({
            forceFocus: true,
            partFileUuid: bestNextSetupPartFileUuid,
            partUuid:
              currentOrderedPartInformation[currentPartPosition + 1]?.uuid ??
              null,
          }),
        );
        // If submit called via keyboard ('enter' or 'space'), focus the first input of next part
        setPartSetupState({ lastChangedBy: changedBy, gotoLast: false });
        changedBy === PartSetupStateChangedBy.Keyboard && setFocusInput(true);
        // If submit called via keyboard ('enter' or 'space'), focus the first input of next part
      } else if (quoteItem?.next) {
        const quoteItemId = quoteItem.next.id;
        //Update history, reset searchTail, and dispatch new searchTail and quoteItemId
        //Note: This will trigger useEffect above that dispatches receiveQuoteItemId
        setPartSetupState({
          quoteItemId,
          lastChangedBy: changedBy,
          gotoLast: false,
        });
      }
    },
    [
      dispatch,
      quoteItem,
      currentOrderedPartInformation,
      currentPartPosition,
      bestNextSetupPartFileUuid,
      setPartSetupState,
    ],
  );

  const handlePreviousClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // keypress gives e.detail = 0, other values indicate mouse click
      handlePreviousItem(
        e.detail === 0
          ? PartSetupStateChangedBy.Keyboard
          : PartSetupStateChangedBy.Mouse,
      );
    },
    [handlePreviousItem],
  );

  const handleNextClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      // keypress gives e.detail = 0, other values indicate mouse click
      handleNextItem(
        e.detail === 0
          ? PartSetupStateChangedBy.Keyboard
          : PartSetupStateChangedBy.Mouse,
      );
    },
    [handleNextItem],
  );

  const isPreviousButtonDisabled = useMemo(
    () => currentPartPosition === 0 && !quoteItem?.previous,
    [quoteItem, currentPartPosition],
  );

  const isNextButtonDisabled = useMemo(
    () =>
      currentOrderedPartInformation.length <= currentPartPosition + 1 &&
      !quoteItem?.next,
    [quoteItem, currentOrderedPartInformation, currentPartPosition],
  );

  if (isLoading) {
    return (
      <VStack {...vstackLayoutProps}>
        <Skeleton w={20} h={2} />
        <HStack {...buttonGroupLayoutProps}>
          <Skeleton h={4} borderRadius="md" {...buttonLayoutProps} />
          <Skeleton h={4} borderRadius="md" {...buttonLayoutProps} />
        </HStack>
      </VStack>
    );
  }

  if (!quoteItem) {
    return <></>;
  }

  return (
    <VStack {...vstackLayoutProps}>
      <Text size="sm">
        Part {currentPartPosition + 1} of {currentOrderedPartInformation.length}{' '}
        for this line item
      </Text>
      <ButtonGroup {...buttonGroupLayoutProps}>
        <Tooltip
          isDisabled={!isPreviousButtonDisabled}
          label="There are no previous line items"
          placement="right"
        >
          <Button
            variant="secondary"
            size="md"
            onClick={handlePreviousClick}
            isDisabled={isPreviousButtonDisabled}
            {...buttonLayoutProps}
          >
            {currentPartPosition > 0 ? 'Previous Part' : 'Previous Line Item'}
          </Button>
        </Tooltip>
        <Tooltip
          isDisabled={!isNextButtonDisabled}
          label="There are no additional line items"
          placement="right"
        >
          <Button
            size="md"
            onClick={handleNextClick}
            isDisabled={isNextButtonDisabled}
            {...buttonLayoutProps}
          >
            {currentOrderedPartInformation.length > currentPartPosition + 1
              ? 'Next Part'
              : 'Next Line Item'}
          </Button>
        </Tooltip>
      </ButtonGroup>
    </VStack>
  );
};
